import { useTargetedAreaWebsocket } from '@/hooks/useTargetedAreaWebsocket'
import { useLocations } from '@/queries/locations/useLocations'
import { useAppDispatch, useAppSelector } from '@redux/hooks/useAppState'
import {
	DEFAULT_LATITUDE,
	DEFAULT_LONGITUDE,
	EXCLUSION_TOOL,
	INCLUSION_TOOL,
} from 'honeygrid-types'
import { Navigation } from 'lucide-react'
import { useRef } from 'react'
import { Layer, Map as MapGL, NavigationControl, Source } from 'react-map-gl/mapbox'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox'

import 'mapbox-gl/dist/mapbox-gl.css'

import { BusinessNameLocationMarker } from '@/components/Business'
import { LoadingBar } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { useResizeObserver } from '@/hooks/useResize'

import { MapLegend } from '../../MapLegend'
import { CellDetailsMarker } from './CellDetailsMarker'
import { CountiesMapLayer } from './CountiesMapLayer'
import { HoneyGridOutlineLayer } from './HoneyGridOutlineLayer'
import { LayerSelector } from './LayerSelector'
import { ManualFilterLayer } from './ManualFilterLayer'

import './Map.css'

import { SyncToGoogleDialog } from '@/components/SyncToGoogleDialog/SyncToGoogleDialog'
import {
	exclusionToolEnabledSelector,
	inclusionToolEnabledSelector,
	selectedReferenceFieldSelector,
} from '@/redux/store/slices/map/mapSlice'
import {
	isSyncToGoogleDialogOpen,
	setIsSyncToGoogleDialogOpen,
} from '@/redux/store/slices/ui/uiSlice'
import { useStytchOrganization } from '@stytch/react/b2b'

import { useMapStyle } from './hooks/useMapStyle'
import { useOnCellClicked } from './hooks/useOnCellClicked'
import { useTargetingPreview } from './hooks/useTargetingPreview'
import { useTargetingSelection } from './hooks/useTargetingSelection'
import { useTargetingSelectionGeoJson } from './hooks/useTargetingSelectionGeoJson'
import { useViewState } from './hooks/useViewState'
import { MapControls } from './MapControls'
import { MapToolButton } from './MapToolButton'
import { ZipCodesMapLayer } from './ZipCodesMapLayer'

const BoostPreviewFillColor = '#81479C'
const BoostPreviewFillOpacity = 0.7
const BoostPreviewLineColor = '#ffffff'
const BoostPreviewLineOpacity = 0.7
const BoostFillColor = '#621984'
const BoostFillOpacity = 0.7
const BoostLineColor = '#621984'
const BoostLineOpacity = 0.7

const MapComponent = () => {
	const { gridId = '' } = useParams()
	const [searchParams] = useSearchParams()
	const location = useLocation()
	const mapRef = useRef<MapRef>(null)
	const selectedGridId = searchParams.get('gridId')
	const selectedReferenceField = useAppSelector(selectedReferenceFieldSelector)
	const inclusionToolEnabled = useAppSelector(inclusionToolEnabledSelector)
	const exclusionToolEnabled = useAppSelector(exclusionToolEnabledSelector)
	const mapStyle = useMapStyle()
	const { isFetching, isSuccess, data: honeygridData } = useTargetedAreaWebsocket()
	const [viewState, setViewState, centerMap] = useViewState(
		isFetching,
		isSuccess,
		honeygridData?.geojson,
		mapRef
	)
	const [clickedCell, handleClickedCell] = useOnCellClicked(
		mapRef,
		honeygridData?.aggregatedDemographics
	)
	const isSyncDialogOpen = useAppSelector(isSyncToGoogleDialogOpen)
	const dispatch = useAppDispatch()

	const targetingSelection = useTargetingSelection(honeygridData?.aggregatedDemographics)
	const targetingSelectionGeoJson = useTargetingSelectionGeoJson(targetingSelection)

	const hasTargetingSelection = !!targetingSelectionGeoJson

	const targetingPreviewGeoJson = useTargetingPreview({
		selectedReferenceField,
		aggregatedDemographics: honeygridData?.aggregatedDemographics,
	})

	const containerRef = useRef<HTMLDivElement>(null)
	useResizeObserver(containerRef, () => {
		mapRef.current?.resize()
	})

	const beforeIdFill = hasTargetingSelection ? { beforeId: 'boosted-honeygrid-fill' } : {}
	const beforeIdLine = hasTargetingSelection ? { beforeId: 'boosted-honeygrid-line' } : {}

	return (
		<>
			<div className="grid grid-cols-1 grid-rows-1 h-full relative">
				{isFetching && !isSuccess && <LoadingBar />}

				<div ref={containerRef} className="grid-area-[map]">
					<MapGL
						{...viewState}
						ref={mapRef}
						onMove={(event: ViewStateChangeEvent) => setViewState(event.viewState)}
						style={{ width: '100%', height: '100%' }}
						mapStyle={mapStyle}
						initialViewState={viewState}
						mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
						projection={{ name: 'globe' }}
						onClick={handleClickedCell}
					>
						<CellDetailsMarker
							cell={clickedCell}
							onClose={() => {
								handleClickedCell(null)
							}}
						/>
						<CountiesMapLayer beforeId={beforeIdFill} />
						<ZipCodesMapLayer beforeId={beforeIdFill} />
						{targetingSelectionGeoJson ? (
							<Source id="boosted-honeygrid" type="geojson" data={targetingSelectionGeoJson}>
								<Layer
									id="boosted-honeygrid-fill"
									type="fill"
									paint={{
										'fill-color': BoostFillColor,
										'fill-opacity': BoostFillOpacity,
									}}
								/>
								<Layer
									id="boosted-honeygrid-line"
									type="line"
									paint={{
										'line-color': BoostLineColor,
										'line-width': 3,
										'line-opacity': BoostLineOpacity,
									}}
								/>
							</Source>
						) : null}
						<HoneyGridOutlineLayer
							beforeIdFill={beforeIdFill}
							beforeIdLine={beforeIdLine}
							honeygridData={honeygridData}
						/>
						<ManualFilterLayer
							manualFilterKey="inclusion"
							cellLookupObject={honeygridData?.aggregatedDemographics?.cells}
						/>
						<ManualFilterLayer
							manualFilterKey="exclusion"
							cellLookupObject={honeygridData?.aggregatedDemographics?.cells}
						/>
						{targetingPreviewGeoJson ? (
							<Source id="boosted-preview-honeygrid" type="geojson" data={targetingPreviewGeoJson}>
								<Layer
									id="selected-honeygrid-fill"
									type="fill"
									paint={{
										'fill-color': BoostPreviewFillColor,
										'fill-opacity': BoostPreviewFillOpacity,
									}}
								/>
								<Layer
									id="selected-honeygrid-line"
									type="line"
									paint={{
										'line-color': BoostPreviewLineColor,
										'line-width': 3,
										'line-opacity': BoostPreviewLineOpacity,
									}}
								/>
							</Source>
						) : null}
						<MapBusinessLocationMarkers />
						<NavigationControl />
					</MapGL>
				</div>
				<div className="grid-area-[map] flex flex-col justify-between">
					{(gridId || selectedGridId || location.pathname.includes('/honeygrid/add')) && (
						<MapControls />
					)}
				</div>
				<div className="absolute right-[10px] top-27 flex flex-col gap-2">
					<Button variant="secondary" size="mapTool" className="cursor-pointer" onClick={centerMap}>
						<Navigation className="h-4 w-4" />
						<span className="sr-only">Recenter map</span>
					</Button>
					<LayerSelector />
					{(gridId || location.pathname.includes('/honeygrid/add')) && (
						<>
							<MapToolButton selected={inclusionToolEnabled} tool={INCLUSION_TOOL} />
							<MapToolButton selected={exclusionToolEnabled} tool={EXCLUSION_TOOL} />
						</>
					)}
				</div>
				{(gridId || selectedGridId || location.pathname.includes('/honeygrid/add')) && (
					<MapLegend
						aggregatedDemographics={honeygridData?.aggregatedDemographics}
						fieldMappings={honeygridData?.fieldMappings}
					/>
				)}
			</div>
			<SyncToGoogleDialog
				isOpen={isSyncDialogOpen}
				onDismiss={() => dispatch(setIsSyncToGoogleDialogOpen(false))}
			/>
		</>
	)
}

const MapBusinessLocationMarkers = () => {
	const { organization } = useStytchOrganization()
	const { data: locations } = useLocations({ workspaceId: organization?.organization_id })

	const locationMarkers = locations
		? locations.map((location) => {
				return (
					<BusinessNameLocationMarker
						key={location.id}
						latitude={Number(location?.latitude ?? DEFAULT_LATITUDE)}
						longitude={Number(location?.longitude ?? DEFAULT_LONGITUDE)}
						name={`${location.name}`}
					/>
				)
			})
		: null
	return <>{locationMarkers}</>
}

export { MapComponent as Map }
